
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TitleBarComponent } from 'src/app/base/shared/title-bar/title-bar.component';

import { StepperAllModule, StepperClickEventArgs, StepperModule } from '@syncfusion/ej2-angular-navigations';
import { FormsModule, FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { VendorScoreCardSurveyFormType } from './vendor-score-card-survey-form-type';
import { StepMarketSizeFormType } from './steps/step-market-size-form-type';
import { StepSalesStructureFormType } from './steps/step-sales-structure-form-type';
import { StepProductPortfolioFormType } from './steps/step-product-portfolio-form-type';
import { StepMarketingAppearanceFormType } from './steps/step-marketing-appearance-form-type';
import { StepPotentialFormType } from './steps/step-potential-form-type';
import { AppErrorMessagesComponent } from 'src/app/base/error-messages/app-error-messages.component';
import { CreateVendorScoreCardSurveyDataDTO, VendorScoreCardRowTypeV20, VendorScoreCardSurveyDataRowDTO } from 'src/app/base/models/models.swagger.generated';
import { VendorScoreCardSurveyService } from './vendor-score-card-survey.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AuthService } from 'src/app/base/services/auth.service';

@Component({
    selector: 'app-vendor-score-card-survey',
    templateUrl: './vendor-score-card-survey.component.html',
    styleUrls: ['./vendor-score-card-survey.component.scss'],
    imports: [TitleBarComponent, TranslateModule, AppErrorMessagesComponent, StepperAllModule, StepperModule, FormsModule, ReactiveFormsModule]
})
export class VendorScoreCardSurveyComponent implements OnInit {
  public areParametersValid = false;
  private accessToken: string;
  public loading: boolean = true;

  public form: FormGroup<VendorScoreCardSurveyFormType>;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private vendorScoreCardSurveyService: VendorScoreCardSurveyService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) { }

  public stepIndex: number = 0;
  public stepMarketSizeNumber: number = 0;
  public stepSalesStructureNumber: number = 1;
  public stepProductPortfolioNumber: number = 2;
  public stepMarketingAppearanceNumber: number = 3;
  public stepPotentialNumber: number = 4;

  public showButtons: boolean = true;

  ngOnInit() {
    this.stepIndex = 0;
    this.route.queryParams.subscribe(params => {
      this.accessToken = params.code;
      if (this.accessToken) {
        this.loading = true;
        this.vendorScoreCardSurveyService.getVendorScoreCardByAccessToken(this.accessToken).subscribe({
          next: (overview) => {
            // get the language from the overview and set it to the translate service
            this.areParametersValid = true;
            this.authService.setUserLanguage(overview.language?.toLowerCase() || 'en');
            this.loading = false;
            this.cdr.detectChanges();
          },
          error: () => {
            this.areParametersValid = false;
            this.loading = false;
            this.cdr.detectChanges();
          }
        });
      }
      this.cdr.detectChanges();
    });

    this.initializeForm();
  }

  private initializeForm(): void {
    this.form = this.fb.group<VendorScoreCardSurveyFormType>({
      marketSize: this.fb.group<StepMarketSizeFormType>({
        tam: this.fb.control(0, [Validators.required, Validators.min(1)]),
        currentMarketShares: this.fb.control(0, [Validators.required, Validators.min(1), Validators.max(100)]),
      }),
      salesStructure: this.fb.group<StepSalesStructureFormType>({
        numberOfActiveResellers: this.fb.control(0, [Validators.required, Validators.min(1)]),
        numberOfDistributors: this.fb.control(0, [Validators.required, Validators.min(0)]),
        shareOfRevenue: this.fb.control(0, [Validators.required, Validators.min(0), Validators.max(100)])
      }),
      productPortfolio: this.fb.group<StepProductPortfolioFormType>({
        numberOfProductCategories: this.fb.control(0, [Validators.required, Validators.min(1)]),
        numberOfProducts: this.fb.control(0, [Validators.required, Validators.min(1)])
      }),
      marketingAppearance: this.fb.group<StepMarketingAppearanceFormType>({
        totalFollowers: this.fb.control(0, [Validators.required, Validators.min(1)]),
        percentageRevenueAllocatedForChannelActivities: this.fb.control(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      }),
      potential: this.fb.group<StepPotentialFormType>({
        projectedNetRevenue: this.fb.control(0, [Validators.required, Validators.min(1)]),
        expectedGrossProfit: this.fb.control(0, [Validators.required, Validators.min(1), Validators.max(100)]),
        revenueGoal: this.fb.control(0, [Validators.required, Validators.min(1)]),
        paymentTerms: this.fb.control(0, [Validators.required, Validators.min(0)])
      })
    });
  }

  public next() {
    this.stepIndex += 1;
  }

  public back() {
    this.stepIndex -= 1;
  }

  public submit() {
    if (this.form.valid) {
      var rows: Array<VendorScoreCardSurveyDataRowDTO> = [
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: null,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.MarketSize
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.marketSize.tam,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.TAM
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.marketSize.currentMarketShares,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.CurrentMarketShares
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: null,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.SalesStructure
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.salesStructure.numberOfActiveResellers,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.NumberOfActiveResellers
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.salesStructure.numberOfDistributors,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.NumberOfDistributors
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.salesStructure.shareOfRevenue,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.ShareOfRevenue
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: null,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.ProductPortfolio
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.productPortfolio.numberOfProductCategories,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.NumberOfProductCategories
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.productPortfolio.numberOfProducts,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.NumberOfProducts
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: null,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.MarketingPresence
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.marketingAppearance.totalFollowers,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.TotalFollowerCount
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.marketingAppearance.percentageRevenueAllocatedForChannelActivities,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.PercentageRevenueAllocatedForChannelActivities
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: null,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.PotentialAndTnC
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.potential.projectedNetRevenue,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.ProjectedNetRevenue
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.potential.expectedGrossProfit,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.ExpectedGrossProfitMargin
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.potential.revenueGoal,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.RevenueGoal
          }
        ),
        new VendorScoreCardSurveyDataRowDTO(
          {
            facts: this.form.value.potential.paymentTerms,
            vendorScoreCardRowType: VendorScoreCardRowTypeV20.PaymentTerms
          }
        )
      ];

      var dto = new CreateVendorScoreCardSurveyDataDTO(
        {
          accessToken: this.accessToken,
          rows: rows
        }
      );
    }

    this.vendorScoreCardSurveyService.addRecord(dto).subscribe({
      next: () => {
        this.showButtons = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.showButtons = true;
        this.cdr.detectChanges();
      }
    })
  }

  public onStepClick(args: StepperClickEventArgs) {
    this.stepIndex = args.activeStep;
  }

  public isStepDisabled(step: number): boolean {
    switch (step) {
      case 0:
        return false;
      case 1:
        if (this.form.controls.marketSize.valid) {
          return false;
        }
        break;
      case 2:
        if (
          this.form.controls.marketSize.valid &&
          this.form.controls.salesStructure.valid
        ) {
          return false;
        }
        break;
      case 3:
        if (
          this.form.controls.marketSize.valid &&
          this.form.controls.salesStructure.valid &&
          this.form.controls.productPortfolio.valid
        ) {
          return false;
        }
        break;
      case 4:
        if (
          this.form.controls.marketSize.valid &&
          this.form.controls.salesStructure.valid &&
          this.form.controls.productPortfolio.valid &&
          this.form.controls.marketingAppearance.valid
        ) {
          return false;
        }
        break;
    }
    return true;
  }

  public getHtml(key: string): SafeHtml {
    const html = this.translate.instant(key);
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
